/**
 * CommissionBreakdownCard Component - Level-by-level breakdown and top affiliates
 */
import React, { useState } from 'react';
import type { CommissionBreakdown, TopAffiliate } from './types';

interface CommissionBreakdownCardProps {
  breakdown: CommissionBreakdown[];
  topAffiliates: TopAffiliate[];
  loading?: boolean;
}

export const CommissionBreakdownCard: React.FC<CommissionBreakdownCardProps> = ({
  breakdown, topAffiliates, loading
}) => {
  const [tab, setTab] = useState<'levels' | 'top'>('levels');

  const totalShares = breakdown.reduce((s, b) => s + b.shares, 0);
  const levelColors = ['bg-green-500', 'bg-blue-500', 'bg-purple-500', 'bg-amber-500', 'bg-red-500'];

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg border border-gray-700 p-4 animate-pulse">
        <div className="h-4 bg-gray-700 rounded w-32 mb-4" />
        <div className="space-y-2">
          {[1, 2, 3].map(i => <div key={i} className="h-8 bg-gray-700 rounded" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
      {/* Header with tabs */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-white font-semibold">💎 Commission Breakdown</h3>
        <div className="flex gap-1 bg-gray-700 rounded-lg p-0.5">
          <button
            onClick={() => setTab('levels')}
            className={`px-3 py-1 rounded text-sm transition-colors ${tab === 'levels' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}`}
          >
            By Level
          </button>
          <button
            onClick={() => setTab('top')}
            className={`px-3 py-1 rounded text-sm transition-colors ${tab === 'top' ? 'bg-purple-600 text-white' : 'text-gray-400 hover:text-white'}`}
          >
            Top Affiliates
          </button>
        </div>
      </div>

      {tab === 'levels' ? (
        <div className="space-y-3">
          {breakdown.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No commission data available</p>
          ) : (
            breakdown.map((level, i) => {
              const percent = totalShares > 0 ? (level.shares / totalShares) * 100 : 0;
              return (
                <div key={level.level}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-white">Level {level.level}</span>
                    <span className="text-white">{level.shares.toLocaleString()} shares (${level.value.toLocaleString()})</span>
                  </div>
                  <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                    <div className={`h-full ${levelColors[i % levelColors.length]} transition-all`} style={{ width: `${percent}%` }} />
                  </div>
                  <p className="text-sm text-gray-500 mt-0.5">{level.count} transactions • {percent.toFixed(1)}% of total</p>
                </div>
              );
            })
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {topAffiliates.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No affiliate data available</p>
          ) : (
            topAffiliates.slice(0, 10).map((affiliate, i) => (
              <div key={affiliate.userId} className="flex items-center gap-3 p-2 bg-gray-700/50 rounded-lg">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${i < 3 ? 'bg-yellow-500 text-black' : 'bg-gray-600 text-white'}`}>
                  {i + 1}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{affiliate.username || affiliate.userId.slice(0, 8)}</p>
                  <p className="text-gray-500 text-sm">{affiliate.referralCount} referrals</p>
                </div>
                <div className="text-right">
                  <p className="text-purple-400 font-bold">${affiliate.totalCommission.toLocaleString()}</p>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default CommissionBreakdownCard;

